import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import chartOfAccountsApi from '../../../api/chartOfAccountsApi';
import axios from '../../../axios'; // For generic lookups like Branch if needed
import { toast } from 'react-toastify';
import SearchableSelect from '../../../components/ui/SearchableSelect';

// Enums Mappings
const AccountNature = [
  { id: 1, name: 'مدين' },
  { id: 2, name: 'دائن' }
];

const AccountTypes = [
  { id: 1, name: 'أصول' },
  { id: 2, name: 'خصوم' },
  { id: 3, name: 'حقوق ملكية' },
  { id: 4, name: 'إيرادات' },
  { id: 5, name: 'مصروفات' }
];

const FinalAccountTypes = [
  { id: 1, name: 'ميزانية' },
  { id: 2, name: 'قائمة الدخل' },
  { id: 3, name: 'حساب مخزون' },
  { id: 4, name: 'ضريبة مشتريات' },
  { id: 5, name: 'ضريبة مبيعات' },
  { id: 6, name: 'مبيعات' },
  { id: 7, name: 'مردود مبيعات' },
  { id: 8, name: 'خصم' }
];

const TaxTypes = [
  { id: 0, name: 'بدون ضريبة' },
  { id: 1, name: 'ضريبة قيمة مضافة' },
  { id: 2, name: 'ضريبة استقطاع' }
];

export default function AddAccountModal({ isOpen, onClose, onSuccess }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [parents, setParents] = useState([]);
  
  // Form State
  const [formData, setFormData] = useState({
    parentId: '',
    code: '',
    nameAr: '',
    nameEn: '',
    nature: 1,
    accountType: 1,
    finalAccountType: '',
    taxType: 0,
    costCenterRequired: false,
    description: '',
    branchId: '', // Optional
    allowPosting: true // Default to Sub
  });

  // Fetch Parents (Main Accounts) on mount
  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      chartOfAccountsApi.getAll()
        .then(res => {
          // Filter only Main accounts (AllowPosting = false) to be parents
          const mains = res.data.filter(a => !a.allowPosting);
          setParents(mains || []);
        })
        .catch(err => console.error("Error fetching parents", err))
        .finally(() => setLoading(false));
    }
  }, [isOpen]);

  // Update Code prefix when Parent changes
  const handleParentChange = (parentId) => {
    const parent = parents.find(p => p.id === parentId);
    
    setFormData(prev => ({
      ...prev,
      parentId,
      // If parent found, inherit its nature/type as default
      // code: parent ? parent.code : prev.code, // Don't just set parent code, wait for next code
      nature: parent ? parent.nature : prev.nature,
      accountType: parent ? parent.accountType : prev.accountType
    }));

    if (parentId) {
      // Fetch next available code
      chartOfAccountsApi.getNextCode(parentId)
        .then(res => {
          if (res.data && res.data.code) {
             setFormData(prev => ({ ...prev, code: res.data.code }));
          }
        })
        .catch(err => {
            console.error("Error fetching next code", err);
            // Fallback: just set parent code if error
            if (parent) {
                setFormData(prev => ({ ...prev, code: parent.code }));
            }
        });
    } else {
        setFormData(prev => ({ ...prev, code: '' }));
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    const parent = parents.find(p => p.id === formData.parentId);
    if (!parent) {
      toast.error("يرجى اختيار الحساب الرئيسي");
      return;
    }
    if (!formData.code.startsWith(parent.code)) {
      toast.error(`كود الحساب يجب أن يبدأ بـ ${parent.code}`);
      return;
    }
    // if (formData.code.length !== 8) {
    //   toast.error("كود الحساب يجب أن يتكون من 8 أرقام");
    //   return;
    // }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        level: parent.level + 1, // Calculate level
        branchId: formData.branchId || null,
        finalAccountType: formData.finalAccountType === "" ? null : Number(formData.finalAccountType)
      };
      
      await chartOfAccountsApi.create(payload);
      toast.success("تم إضافة الحساب بنجاح");
      onSuccess?.();
      onClose();
      // Reset form
      setFormData({
        parentId: '',
        code: '',
        nameAr: '',
        nameEn: '',
        nature: 1,
        accountType: 1,
        finalAccountType: '',
        taxType: 0,
        costCenterRequired: false,
        description: '',
        branchId: ''
      });
    } catch (err) {
      const msg = err.response?.data?.errors ? JSON.stringify(err.response.data.errors) : (err.response?.data || "Error saving account");
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b bg-gray-50 rounded-t-lg">
          <h3 className="text-xl font-bold text-gray-800">إضافة حساب جديد (مستوى 5)</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-red-500 text-2xl">&times;</button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          
          {/* Parent & Code */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">الحساب الرئيسي (الأب) <span className="text-red-500">*</span></label>
              <SearchableSelect
                label="اختر الحساب الرئيسي"
                options={parents}
                value={formData.parentId}
                onChange={handleParentChange}
                getOptionLabel={(option) => `${option.code} - ${option.nameAr}`}
                getOptionValue={(option) => option.id}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">رقم الحساب <span className="text-red-500">*</span></label>
              <input
                type="text"
                name="code"
                value={formData.code}
                onChange={handleChange}
                //maxLength={8}
                className="w-full border border-gray-300 rounded-md p-2 focus:ring-2 focus:ring-blue-500"
                placeholder="سيتم توليده تلقائياً"
                required
              />
              <p className="text-xs text-gray-500 mt-1">يجب أن يبدأ برقم الحساب الرئيسي</p>
            </div>
          </div>

          {/* Account Kind (Main/Sub) */}
          <div className="mb-4">
             <label className="block text-sm font-medium text-gray-700 mb-1">نوع الحساب (رئيسي/فرعي) <span className="text-red-500">*</span></label>
             <div className="flex gap-4 mt-2">
                <label className="flex items-center cursor-pointer">
                  <input 
                    type="radio" 
                    name="allowPosting" 
                    checked={!formData.allowPosting} 
                    onChange={() => setFormData(prev => ({ ...prev, allowPosting: false }))}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="mr-2">رئيسي (Main)</span>
                </label>
                <label className="flex items-center cursor-pointer">
                  <input 
                    type="radio" 
                    name="allowPosting" 
                    checked={formData.allowPosting} 
                    onChange={() => setFormData(prev => ({ ...prev, allowPosting: true }))}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="mr-2">فرعي (Sub)</span>
                </label>
             </div>
             <p className="text-xs text-gray-500 mt-1">
               {formData.allowPosting ? "يسمح بإجراء قيود عليه (حركة)" : "حساب تجميعي لا يتم القيد عليه مباشرة"}
             </p>
          </div>

          {/* Names */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">الاسم بالعربي <span className="text-red-500">*</span></label>
              <input
                type="text"
                name="nameAr"
                value={formData.nameAr}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md p-2 focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">الاسم بالإنجليزي <span className="text-red-500">*</span></label>
              <input
                type="text"
                name="nameEn"
                value={formData.nameEn}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md p-2 focus:ring-2 focus:ring-blue-500 text-left"
                required
              />
            </div>
          </div>

          {/* Types */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">طبيعة الحساب <span className="text-red-500">*</span></label>
              <select
                name="nature"
                value={formData.nature}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md p-2"
                required
              >
                {AccountNature.map(n => <option key={n.id} value={n.id}>{n.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">نوع الحساب <span className="text-red-500">*</span></label>
              <select
                name="accountType"
                value={formData.accountType}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md p-2"
                required
              >
                {AccountTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">الحساب الختامي</label>
              <select
                name="finalAccountType"
                value={formData.finalAccountType}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md p-2"
              >
                <option value="">-- اختياري --</option>
                {FinalAccountTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
          </div>

          {/* Additional Options */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 p-3 rounded-lg border">
             <div>
               <label className="block text-sm font-medium text-gray-700 mb-1">نوع الضريبة</label>
               <select
                  name="taxType"
                  value={formData.taxType}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md p-2"
                >
                  {TaxTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
               </select>
             </div>
             <div className="flex items-center mt-6">
                <input
                  type="checkbox"
                  name="costCenterRequired"
                  id="costCenterRequired"
                  checked={formData.costCenterRequired}
                  onChange={handleChange}
                  className="h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="costCenterRequired" className="mr-2 block text-sm font-bold text-gray-700">
                  يتطلب مركز تكلفة؟
                </label>
             </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">ملاحظات / وصف</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="2"
              className="w-full border border-gray-300 rounded-md p-2"
            ></textarea>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t mt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition"
              disabled={loading}
            >
              إلغاء
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-blue-600 text-white font-bold rounded-md hover:bg-blue-700 transition flex items-center"
              disabled={loading}
            >
              {loading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  جاري الحفظ...
                </>
              ) : "حفظ الحساب"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
